import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { BlockUIModule } from 'primeng/blockui';
import { Card } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Message } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { LoadingService } from './_core/_services/loading.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStore } from './public/private/_store/user.store';
import { _Tipologia_Profilo_Enum } from './_core/_graphql/generated';
import { Subscription } from 'rxjs';
import {
  NgcCookieConsentService,
  NgcInitializationErrorEvent,
  NgcInitializingEvent,
  NgcNoCookieLawEvent,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    Card,
    RouterOutlet,
    Message,
    BlockUIModule,
    ProgressSpinnerModule,
    ToastModule,
    ConfirmDialogModule,
    TranslatePipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  // subscriptions
  private popupOpenSubscription?: Subscription;
  private popupCloseSubscription?: Subscription;
  private initializingSubscription?: Subscription;
  private initializedSubscription?: Subscription;
  private initializationErrorSubscription?: Subscription;
  private statusChangeSubscription?: Subscription;
  private revokeChoiceSubscription?: Subscription;
  private noCookieLawSubscription?: Subscription;

  private _translate = inject(TranslateService);
  loading = inject(LoadingService);
  readonly userStore = inject(UserStore);

  constructor(
    route: ActivatedRoute,
    private ccService: NgcCookieConsentService,
  ) { }

  ngOnInit() {
    registerLocaleData(localeIt, 'it-IT');
    localStorage.setItem('lang', 'it');

    const user = localStorage.getItem('user') ?? 'prosumer';
    localStorage.setItem('user', user);

    this.userStore.setType(user as _Tipologia_Profilo_Enum);

    const ref = new URLSearchParams(window.location.search).get('ref');

    if (ref) localStorage.setItem('ref', ref);

    const referral = new URLSearchParams(window.location.search).get('r');

    if (referral) {
      // localStorage.setItem('referral', referral);
      // localStorage.setItem('referral_time', new Date().toISOString());
      localStorage.removeItem('referral');
      localStorage.removeItem('referral_time');
    } else {
      localStorage.removeItem('referral');
      localStorage.removeItem('referral_time');
    }

    // =========================
    // COOKIECONSENT TRANSLATION
    // =========================
    this._translate
      .get([
        'cookie.header',
        'cookie.message',
        'cookie.dismiss',
        'cookie.allow',
        'cookie.deny',
        'cookie.link',
        'cookie.policy',
      ])
      .subscribe((data) => {
        const config = this.ccService.getConfig();
        config.content = config.content || {};

        config.content.header = data['cookie.header'];
        config.content.message = data['cookie.message'];
        config.content.dismiss = data['cookie.dismiss'];
        config.content.allow = data['cookie.allow'];
        config.content.deny = data['cookie.deny'];
        config.content.link = data['cookie.link'];
        config.content.policy = data['cookie.policy'];

        // 🔥 sempre reinizializza così la traduzione è sempre corretta
        this.ccService.destroy();
        this.ccService.init(config);
      });

    // =========================
    // COOKIECONSENT EVENTS
    // =========================
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => { });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(
      () => { },
    );

    this.initializingSubscription = this.ccService.initializing$.subscribe(
      (event: NgcInitializingEvent) => {
        console.log(`initializing: ${JSON.stringify(event)}`);
      },
    );

    this.initializedSubscription = this.ccService.initialized$.subscribe(() => {
      if (
        this.ccService.hasAnswered() &&
        (this.ccService.getStatus() as any) === 'allow'
      ) {
        if (environment.production) {
          this.loadClarity();
          this.loadContentsquare();
        }
      }
    });

    this.initializationErrorSubscription =
      this.ccService.initializationError$.subscribe(
        (event: NgcInitializationErrorEvent) => {
          console.log(
            `initializationError: ${JSON.stringify(event.error?.message)}`,
          );
        },
      );

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        if (event.status === 'allow' && environment.production) {
          this.loadClarity();
          this.loadContentsquare();
        }

        // se revoca il consenso, reload per fermare tracking
        if (event.status === 'deny') {
          window.location.reload();
        }
      },
    );

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(
      () => {
        // quando revoca scelta, di solito viene emesso anche statusChange
      },
    );

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe(
      (event: NgcNoCookieLawEvent) => {
        console.log(`noCookieLaw: ${JSON.stringify(event)}`);
      },
    );
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const loader = document.getElementById('bootstrap-loader');
      if (loader) loader.style.display = 'none';
    }, 500);
  }

  checkReferralExpiration(): void {
    const referralTime = localStorage.getItem('referral_time');

    if (referralTime) {
      const savedTime = new Date(referralTime).getTime();
      const now = new Date().getTime();
      const diffInMinutes = (now - savedTime) / (1000 * 60);

      if (diffInMinutes > 10) {
        localStorage.removeItem('referral');
        localStorage.removeItem('referral_time');
      }
    }
  }

  ngOnDestroy() {
    this.popupOpenSubscription?.unsubscribe();
    this.popupCloseSubscription?.unsubscribe();
    this.initializingSubscription?.unsubscribe();
    this.initializedSubscription?.unsubscribe();
    this.initializationErrorSubscription?.unsubscribe();
    this.statusChangeSubscription?.unsubscribe();
    this.revokeChoiceSubscription?.unsubscribe();
    this.noCookieLawSubscription?.unsubscribe();
  }

  // =========================
  // SCRIPT LOADER
  // =========================
  private loadScript(src: string): void {
    if (document.querySelector(`script[src="${src}"]`)) return;

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  private loadClarity(): void {
    if ((window as any).clarity) return;

    (function (c: any, l: any, a: any, r: any, i: any, t?: any, y?: any) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'vc2i9f2dy0');
  }

  private loadContentsquare(): void {
    this.loadScript('https://t.contentsquare.net/uxa/e33a2ef4702d1.js');
  }
}
